import { useState } from 'react';
import {
  ChecklistItem,
  JobConfig,
  JobConfigRequest,
  JobType,
  TaskConfigRequest,
  TaskType,
} from '../../../clients/quipli-service/job-tasks/job-tasks';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { isEqual } from 'lodash';
import { useJobTaskApi } from '../../../clients/quipli-service/job-tasks/job-task-api';
import { toast } from '../../../util/toasts';
import { Field, FieldLabel } from '../../../components/field/field';
import { SelectForm } from '../../../components/select-form/select-form';
import Icon from '../../../components/icons/icon';
import './job-configs.scss';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { v4 as uuidv4 } from 'uuid';
import { TextArea } from '../../../components/textarea/textarea';
import { Modal } from '../../../components/modal/modal';
import { DataTable } from '../../../components/table/data-table';
import { Toggle } from '../../../components/form/toggle';

export const jobTypeOptions = [
  { value: JobType.CheckinInspection, label: 'Checkin Inspection' },
  { value: JobType.CheckoutInspection, label: 'Checkout Inspection' },
];

export const EditJobConfig = ({
  jobConfig,
  close,
  isDuplicating = false,
}: {
  jobConfig: JobConfig | null;
  close: (updated?: JobConfig) => void;
  isDuplicating?: boolean;
}) => {
  const [step, setStep] = useState(0);
  const [jobConfigState, setJobConfigState] = useState<JobConfigRequest>(
    jobConfig ?? {
      name: '',
      jobType: JobType.CheckoutInspection,
      taskConfigs: [
        {
          header: 'Checklist',
          type: TaskType.InspectionChecklist,
          enabled: true,
          required: false,
          metadata: { checklist: [] },
        },
        { header: 'Machine Hours & Fuel', type: TaskType.MachineHours, enabled: true, required: false, metadata: {} },
        {
          header: 'Upload Media',
          type: TaskType.MediaUpload,
          enabled: true,
          required: false,
          metadata: { mediaSections: [] },
        },
      ],
    },
  );
  const [checklistState, setChecklistState] = useState<ChecklistItem[]>(
    jobConfig?.taskConfigs.find((tc) => tc.type === TaskType.InspectionChecklist)?.metadata?.checklist ?? [],
  );
  const [mediaSections, setMediaSections] = useState<string[]>(
    jobConfig?.taskConfigs.find((tc) => tc.type === TaskType.MediaUpload)?.metadata?.mediaSections ?? [],
  );
  const [enableChecklist, setEnableChecklist] = useState<boolean>(
    jobConfig?.taskConfigs?.find((tc) => tc.type === TaskType.InspectionChecklist)?.enabled ?? true,
  );
  const [enableMachineHours, setEnableMachineHours] = useState<boolean>(
    jobConfig?.taskConfigs?.find((tc) => tc.type === TaskType.MachineHours)?.enabled ?? true,
  );
  const [enableMediaUpload, setEnableMediaUpload] = useState<boolean>(
    jobConfig?.taskConfigs?.find((tc) => tc.type === TaskType.MediaUpload)?.enabled ?? true,
  );

  const { upsertJobConfig } = useJobTaskApi();

  const equalTaskConfigs = (a?: TaskConfigRequest, b?: TaskConfigRequest): boolean => {
    return (
      (!a && !b) ||
      (!!a &&
        !!b &&
        a.type === b.type &&
        a.header === b.header &&
        a.instructions === b.instructions &&
        isEqual(a.metadata, b.metadata) &&
        a.enabled === b.enabled &&
        a.required === b.required)
    );
  };

  const tcToUpdated = (tc: TaskConfigRequest) => {
    if (tc.type === TaskType.InspectionChecklist) {
      return { ...tc, enabled: enableChecklist, metadata: { ...tc.metadata, checklist: checklistState } };
    }
    if (tc.type === TaskType.MediaUpload) {
      return { ...tc, enabled: enableMediaUpload, metadata: { ...tc.metadata, mediaSections } };
    }
    return { ...tc, enabled: enableMachineHours };
  };

  const save = async () => {
    if (!jobConfigState.name || !jobConfigState.jobType || !jobConfigState.taskConfigs) return;

    try {
      const body = {
        ...jobConfigState,
        taskConfigs: jobConfigState.taskConfigs.map(tcToUpdated),
      };

      if (body.taskConfigs.every((tc) => !tc.enabled)) {
        toast('At least one task must be enabled', 'error');
        return;
      }

      const changedNameOrType =
        jobConfigState.name !== jobConfig?.name || jobConfigState.jobType !== jobConfig?.jobType;

      if (
        body.taskConfigs.every((tc) =>
          equalTaskConfigs(
            tc,
            jobConfig?.taskConfigs.find((c) => c.type === tc.type),
          ),
        ) &&
        !changedNameOrType
      ) {
        close(jobConfig);
        return; // no actual updates being made...
      }

      const updated = await upsertJobConfig(body);
      close(updated);
    } catch (e) {
      console.error(e);
      toast('Error saving inspection workflow', 'error');
    }
  };

  const onDuplicate = async () => {
    try {
      const toBeDuped = {
        ...jobConfigState,
        id: '',
        name: jobConfigState.name,
        taskConfigs: jobConfigState.taskConfigs.map((tc) => {
          const base = tcToUpdated(tc);

          return { ...base, id: '' };
        }),
      };

      if (!toBeDuped) {
        return;
      }
      const newJob = await upsertJobConfig(toBeDuped);
      await close(newJob);
    } catch (e: unknown) {
      let errorMsg = 'Failed to duplicate job config';
      if (typeof e === 'object' && e !== null && 'message' in e) {
        errorMsg = (e as { message: string }).message;
      }

      toast(errorMsg, 'error');
    }
  };

  const isNextDisabled = () => {
    switch (step) {
      case 0:
        return !jobConfigState.name || !jobConfigState.jobType;
      case 2:
        return (
          enableMachineHours && !jobConfigState.taskConfigs.find((tc) => tc.type === TaskType.MachineHours)!.header
        );
      case 3:
        return (
          enableMediaUpload &&
          (!jobConfigState.taskConfigs.find((tc) => tc.type === TaskType.MediaUpload)!.header || !mediaSections.length)
        );
      default:
        return false;
    }
  };

  return (
    <Modal isOpen={true} close={() => close(jobConfig ?? undefined)}>
      <div className="edit-job-config">
        {step === 0 ? (
          <div className="flex flex-col gap-4">
            <Typography type="text-lg" weight="semi-bold" className="mb-4">
              {jobConfig ? 'Edit' : 'Create'} Inspection Workflow
            </Typography>
            <Field
              type="text"
              label="Name"
              isRequired={true}
              value={jobConfigState?.name}
              onChange={(name) => setJobConfigState({ ...jobConfigState, name })}
            />
            <SelectForm
              label="Inspection Type"
              options={jobTypeOptions}
              value={jobTypeOptions.find((o) => o.value === jobConfigState?.jobType)}
              onChange={(v) => setJobConfigState({ ...jobConfigState, jobType: v?.value as JobType })}
            />
          </div>
        ) : step === 1 ? (
          <div className="inspection-task-config">
            <Typography type="text-lg" weight="medium" className="mb-4">
              Inspection Checklist Task
            </Typography>

            <div className="flex align-center justify-start mt-2 mb-2 gap-2">
              <FieldLabel label={'Enabled'} labelType={'text-sm'} labelWeight={'medium'} isRequired={true} />
              <Toggle
                color="var(--quipli-yellow-300)"
                data-cy="inspection-task-config-enabled-toggle"
                checked={enableChecklist}
                onClick={() => setEnableChecklist(!enableChecklist)}
              />
            </div>

            <HeaderInstructions
              taskConfig={jobConfigState.taskConfigs?.find((tc) => tc.type === TaskType.InspectionChecklist)}
              setTaskConfig={(tc) => {
                setJobConfigState(
                  (prev) =>
                    prev && {
                      ...prev,
                      taskConfigs: (prev.taskConfigs ?? [])
                        .filter((ptc) => ptc.type !== TaskType.InspectionChecklist)
                        .concat(tc),
                    },
                );
              }}
            />
            <EditChecklist
              enabled={enableChecklist}
              checklist={checklistState}
              onComplete={(checklist) => {
                setChecklistState(checklist);
              }}
              setStep={setStep}
            />
          </div>
        ) : step === 2 ? (
          <div className="machine-hours-task-config">
            <Typography type="text-lg" weight="medium" className="mb-4">
              Machine Hours & Fuel Readings Task
            </Typography>

            <div className="flex align-center justify-start mt-2 mb-2 gap-2">
              <FieldLabel label={'Enabled'} labelType={'text-sm'} labelWeight={'medium'} isRequired={true} />
              <Toggle
                color="var(--quipli-yellow-300)"
                data-cy="inspection-task-config-enabled-toggle"
                checked={enableMachineHours}
                onClick={() => setEnableMachineHours(!enableMachineHours)}
              />
            </div>

            <HeaderInstructions
              taskConfig={jobConfigState.taskConfigs?.find((tc) => tc.type === TaskType.MachineHours)}
              setTaskConfig={(tc) => {
                setJobConfigState((prev) => {
                  return (
                    prev && {
                      ...prev,
                      taskConfigs: (prev.taskConfigs ?? [])
                        .filter((ptc) => ptc.type !== TaskType.MachineHours)
                        .concat(tc),
                    }
                  );
                });
              }}
            />
          </div>
        ) : step === 3 ? (
          <div className="media-upload-task-config">
            <Typography type="text-lg" weight="medium" className="mb-4 mr-4">
              Upload Media Task
            </Typography>

            <div className="flex align-center justify-start mt-2 mb-2 gap-2">
              <FieldLabel label={'Enabled'} labelType={'text-sm'} labelWeight={'medium'} isRequired={true} />
              <Toggle
                color="var(--quipli-yellow-300)"
                data-cy="inspection-task-config-enabled-toggle"
                checked={enableMediaUpload}
                onClick={() => setEnableMediaUpload(!enableMediaUpload)}
              />
            </div>

            <HeaderInstructions
              taskConfig={jobConfigState.taskConfigs?.find((tc) => tc.type === TaskType.MediaUpload)}
              setTaskConfig={(tc) =>
                setJobConfigState((prev) => {
                  return (
                    prev && {
                      ...prev,
                      taskConfigs: (prev.taskConfigs ?? [])
                        .filter((ptc) => ptc.type !== TaskType.MediaUpload)
                        .concat(tc),
                    }
                  );
                })
              }
            />

            <EditMediaSections sections={mediaSections} setSections={setMediaSections} />
          </div>
        ) : (
          <></>
        )}

        {step !== 1 && ( // checklist task config screen has own next button b/c checklist state management
          <div className="flex justify-between gap-4 mt-6">
            <Button
              onClick={() => setStep((prevStep) => prevStep - 1)}
              disabled={step === 0}
              className="w-full button button-secondary"
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                step === 3 ? (isDuplicating ? onDuplicate() : save()) : setStep((prevStep) => prevStep + 1)
              }
              disabled={isNextDisabled()}
              data-testid="next-button"
              className="button button-primary w-full"
            >
              {step === 3 ? 'Save' : 'Next'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const HeaderInstructions = ({
  taskConfig,
  setTaskConfig,
}: {
  taskConfig?: TaskConfigRequest;
  setTaskConfig: (tc: any) => void;
}) => {
  return (
    <div>
      <Field
        className="mb-4"
        type="text"
        isRequired={true}
        label="Header"
        value={taskConfig?.header}
        onChange={(header) => setTaskConfig({ ...taskConfig, header: header.slice(0, 25) })} // 25 char limit
      />
      <TextArea
        style={{ minHeight: '99px', height: '99px' }}
        label="Instructions"
        className="instructions-input text-sm"
        placeholder="Enter task instructions..."
        value={taskConfig?.instructions}
        onChange={(e) => setTaskConfig({ ...taskConfig, instructions: e.target.value })}
        maxLength={100}
      />
    </div>
  );
};

const EditChecklist = ({
  enabled,
  checklist,
  onComplete,
  setStep,
}: {
  enabled: boolean;
  checklist: ChecklistItem[];
  onComplete: (checklist: ChecklistItem[]) => void;
  setStep: (s: number) => void;
}) => {
  const [items, setItems] = useState<(ChecklistItem & { id: string })[]>(
    checklist
      ? checklist.map((i) => ({ ...i, id: i.id ?? uuidv4() }))
      : [{ id: uuidv4(), text: '', isBoolean: true, required: true, media: [] }],
  );

  const data = items.map((item, idx) => {
    const id = uuidv4();
    return {
      mobileHeader: (
        <div className="flex gap-4">
          <Typography type="text-md" weight="bold">
            {item.text}
          </Typography>
        </div>
      ),
      data: item,
      fields: {
        id,
        text: (
          <div className="checklist-item-input">
            <input
              id={`text-input-${id}`}
              placeholder="Ex: Double check the unit number"
              className="w-full items-center"
              type="text"
              value={item.text}
              onChange={(e) => {
                setItems(
                  items
                    .slice(0, idx) // TODO: put this in a helper function or somewhere more reusable
                    .concat([{ ...item, text: e.target.value, media: [] }])
                    .concat(items.slice(idx + 1)),
                );
              }}
            />
          </div>
        ),
        required: (
          <Checkbox
            className="checkbox-center"
            isChecked={item.required}
            onChange={(v) =>
              setItems(
                items
                  .slice(0, idx)
                  .concat([{ ...item, required: v }])
                  .concat(items.slice(idx + 1)),
              )
            }
          />
        ),
        delete: <Icon icon="Trash" onClick={() => setItems(items.filter((i) => i !== item))} />,
      },
    };
  });

  return (
    <div className="mb-8 mt-4">
      <div className="flex justify-between text-grey-700">
        <Typography type="text-md" weight="medium">
          Checklist Items
        </Typography>
      </div>
      <DataTable
        noDataText="No checklist items"
        isLoading={false}
        showAlwaysDesktop={true}
        data={data}
        columnNames={[
          { key: 'text', className: 'w-full items-center', label: '', showRule: 'Both' },
          { key: 'required', className: 'justify-center w-[40px]', label: 'Required', showRule: 'Both', hide: false },
          { key: 'delete', className: 'justify-center w-[40px]', label: '', showRule: 'Both' },
        ]}
      />
      <div
        className="flex gap-2 my-2 text-gray-500 text-sm underline cursor-pointer"
        onClick={() => setItems([...items, { id: uuidv4(), text: '', isBoolean: true, required: true, media: [] }])}
      >
        <Icon icon="Plus" />
        Add Checklist Item
      </div>
      <div className="mt-8 flex justify-between gap-4">
        <Button onClick={() => setStep(0)} className="button button-secondary w-full">
          Previous
        </Button>
        <Button
          onClick={() => {
            if (enabled) {
              onComplete(items);
            }
            setStep(2);
          }}
          disabled={enabled && items.length === 0}
          className="w-full button button-primary"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const EditMediaSections = ({
  sections,
  setSections,
}: {
  sections: string[];
  setSections: (value: React.SetStateAction<string[]>) => void;
}) => {
  const data = sections.map((section, idx) => {
    const id = uuidv4();
    return {
      mobileHeader: (
        <div className="flex gap-4">
          <Typography type="text-md" weight="bold">
            {section}
          </Typography>
        </div>
      ),
      data: section,
      fields: {
        id,
        text: (
          <div className="checklist-item-input">
            <input
              id={`text-input-${id}`}
              placeholder="Ex: Front, Back"
              className="w-full items-center"
              type="text"
              value={section}
              onChange={(e) => {
                setSections((sections) =>
                  sections
                    .slice(0, idx)
                    .concat(e.target.value)
                    .concat(sections.slice(idx + 1)),
                );
              }}
            />
          </div>
        ),
        delete: <Icon icon="Trash" onClick={() => setSections((sections) => sections.filter((i) => i !== section))} />,
      },
    };
  });

  return (
    <div className="mb-8 mt-4">
      <div className="flex justify-between text-grey-700">
        <Typography type="text-md" weight="medium">
          Media Sections
        </Typography>
      </div>
      <DataTable
        noDataText="No media sections"
        isLoading={false}
        showAlwaysDesktop={true}
        data={data}
        columnNames={[
          { key: 'text', className: 'w-full items-center', label: '', showRule: 'Both' },
          { key: 'delete', className: 'justify-center w-[40px]', label: '', showRule: 'Both' },
        ]}
      />
      <div
        className="flex gap-2 my-2 text-gray-500 text-sm underline cursor-pointer"
        onClick={() => setSections([...sections, ''])}
      >
        <Icon icon="Plus" />
        Add Section
      </div>
    </div>
  );
};
